import featureReleases from '@/renderer/data/featureReleases.json';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Sparkles, X } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from './Button';

const LAST_SEEN_VERSION_KEY = 'smartpos_feature_release_last_seen_version';

function compareVersions(a: string, b: string): number {
  const partsA = a.split('.').map(Number);
  const partsB = b.split('.').map(Number);
  for (let i = 0; i < Math.max(partsA.length, partsB.length); i += 1) {
    const diff = (partsA[i] ?? 0) - (partsB[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

function getLastSeenVersion(): string | null {
  try {
    return localStorage.getItem(LAST_SEEN_VERSION_KEY);
  } catch {
    return null;
  }
}

function rememberVersionSeen(version: string): void {
  try {
    localStorage.setItem(LAST_SEEN_VERSION_KEY, version);
  } catch {
    // localStorage unavailable — non-fatal; the modal just reappears next visit.
  }
}

function hasNewVersion(): boolean {
  const lastSeen = getLastSeenVersion();
  if (!lastSeen) return true;
  return compareVersions(featureReleases.version, lastSeen) > 0;
}

interface FeatureReleaseModalProps {
  /** Suppress rendering even if this modal would otherwise be eligible to
   * show — used to keep root-level modals from ever visually colliding.
   * See App.tsx's priority chain: SystemClockWarning > DesktopAppDownloadDialog
   * > FeatureReleaseModal. */
  suppressed?: boolean;
}

/**
 * Shown once per app version, to signed-in users only, stepping through the
 * features added in that release one at a time (data-driven from
 * featureReleases.json). Whoever bumps RELEASE_NOTES.md should add a
 * matching entry here.
 *
 * Mount once near the root of the app (App.tsx), after SystemClockWarning and
 * DesktopAppDownloadDialog so it can be suppressed by either.
 */
export const FeatureReleaseModal: React.FC<FeatureReleaseModalProps> = ({
  suppressed = false,
}) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const [isOpen, setIsOpen] = useState(() => hasNewVersion());
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const prefersReducedMotion = useReducedMotion();

  if (!currentUser || !isOpen || suppressed) return null;

  const { features } = featureReleases;
  const isLastSlide = activeIndex === features.length - 1;
  const hasMultipleSlides = features.length > 1;

  const handleDismiss = () => {
    rememberVersionSeen(featureReleases.version);
    setIsOpen(false);
  };

  const goToSlide = (index: number) => {
    setDirection(index > activeIndex ? 1 : -1);
    setActiveIndex(index);
  };

  const handleNext = () => {
    if (isLastSlide) {
      handleDismiss();
      return;
    }
    goToSlide(activeIndex + 1);
  };

  const handlePrev = () => {
    if (activeIndex === 0) return;
    goToSlide(activeIndex - 1);
  };

  const dialogTransition = prefersReducedMotion
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 420, damping: 38 };

  const slideTransition = prefersReducedMotion
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 380, damping: 34 };

  const activeFeature = features[activeIndex];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleDismiss}
          aria-hidden="true"
        />

        <motion.div
          className="relative flex w-full max-w-md flex-col rounded-2xl bg-white shadow-2xl max-h-[90vh]"
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={dialogTransition}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-pos-primary/10 text-pos-primary">
                <Sparkles size={20} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-pos-primary">
                  What&apos;s new
                </p>
                <h2 className="text-lg font-bold text-gray-900">
                  Version {featureReleases.version}
                </h2>
              </div>
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Close"
              className="shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-500"
            >
              <X size={20} />
            </button>
          </div>

          <div className="relative overflow-hidden">
            <div className="flex items-center gap-2">
              {hasMultipleSlides && (
                <button
                  type="button"
                  onClick={handlePrev}
                  disabled={activeIndex === 0}
                  aria-label="Previous feature"
                  className="shrink-0 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-30"
                >
                  <ChevronLeft size={20} />
                </button>
              )}

              <div className="custom-scrollbar relative min-h-[7rem] flex-1 overflow-hidden">
                <AnimatePresence mode="wait" custom={direction} initial={false}>
                  <motion.div
                    key={activeFeature.title + activeIndex}
                    custom={direction}
                    initial={{ opacity: 0, x: 24 * direction }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 * direction }}
                    transition={slideTransition}
                    className="bg-gray-50 p-4"
                  >
                    <h3 className="text-sm font-semibold text-gray-900">
                      {activeFeature.title}
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      {activeFeature.description}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {hasMultipleSlides && (
                <button
                  type="button"
                  onClick={() => goToSlide(activeIndex + 1)}
                  disabled={isLastSlide}
                  aria-label="Next feature"
                  className="shrink-0 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-30"
                >
                  <ChevronRight size={20} />
                </button>
              )}
            </div>

            {hasMultipleSlides && (
              <div className="mt-4 flex items-center justify-center gap-1.5">
                {features.map((feature, index) => (
                  <button
                    key={`${feature.title}-${feature.description}`}
                    type="button"
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to feature ${index + 1}`}
                    className={`h-1.5 rounded-full transition-all ${
                      index === activeIndex
                        ? 'w-4 bg-pos-primary'
                        : 'w-1.5 bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end border-t border-gray-100 p-6">
            <Button onClick={handleNext}>
              {isLastSlide ? 'Got it' : 'Next'}
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
